import React, { useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonAlert,
  IonSpinner,
  IonText,
  IonBadge,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
} from "@ionic/react";
import {
  refreshOutline,
  timeOutline,
  calendarOutline,
  checkmarkCircleOutline,
  alertCircleOutline,
  playOutline,
  stopOutline,
} from "ionicons/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import api from "../services/api";
import { useI18n } from "@/i18n";

interface CronJobStatus {
  name: string;
  lastRun: string | null;
  nextRun: string | null;
  status: "running" | "idle" | "error";
  description: string;
}

const AdminDashboard: React.FC = () => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedJob, setSelectedJob] = useState<string>("");
  const queryClient = useQueryClient();
  const { t } = useI18n();

  // Fetch cron job status
  const { data: cronStatus } = useQuery(
    "cronStatus",
    async () => {
      const response = await api.get("/api/admin/cron/status");
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds to avoid rate limiting
      retry: (failureCount, error: any) => {
        // Don't retry on rate limit errors
        if (error?.response?.status === 429) {
          return false;
        }
        return failureCount < 3;
      },
    }
  );

  // Manual cron job execution
  const executeCronMutation = useMutation(
    async (jobName: string) => {
      const response = await api.post(`/api/admin/cron/execute/${jobName}`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setAlertMessage(`✅ ${data.message}`);
        setShowAlert(true);
        queryClient.invalidateQueries("cronStatus");
      },
      onError: (error: any) => {
        if (error.response?.status === 429) {
          setAlertMessage(`❌ ${t("rateLimitExceeded")}`);
        } else {
          setAlertMessage(
            `❌ ${t("errorPrefix")}: ${
              error.response?.data?.message || error.message
            }`
          );
        }
        setShowAlert(true);
      },
    }
  );

  // Stop all cron jobs
  const stopCronMutation = useMutation(
    async () => {
      const response = await api.post("/api/admin/cron/stop");
      return response.data;
    },
    {
      onSuccess: (data) => {
        setAlertMessage(`✅ ${data.message}`);
        setShowAlert(true);
        queryClient.invalidateQueries("cronStatus");
      },
      onError: (error: any) => {
        if (error.response?.status === 429) {
          setAlertMessage(`❌ ${t("rateLimitExceeded")}`);
        } else {
          setAlertMessage(
            `❌ ${t("errorPrefix")}: ${
              error.response?.data?.message || error.message
            }`
          );
        }
        setShowAlert(true);
      },
    }
  );

  const handleExecuteJob = (jobName: string) => {
    setSelectedJob(jobName);
    executeCronMutation.mutate(jobName);
  };

  const handleStopAll = () => {
    stopCronMutation.mutate();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "success";
      case "error":
        return "danger";
      default:
        return "medium";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "running":
        return playOutline;
      case "error":
        return alertCircleOutline;
      default:
        return checkmarkCircleOutline;
    }
  };

  const cronJobs: CronJobStatus[] = [
    {
      name: "score-calculation",
      lastRun: cronStatus?.scoreCalculation?.lastRun || null,
      nextRun: cronStatus?.scoreCalculation?.nextRun || null,
      status: cronStatus?.scoreCalculation?.status || "idle",
      description: t("cronDescriptionScore"),
    },
    {
      name: "fixture-update",
      lastRun: cronStatus?.fixtureUpdate?.lastRun || null,
      nextRun: cronStatus?.fixtureUpdate?.nextRun || null,
      status: cronStatus?.fixtureUpdate?.status || "idle",
      description: t("cronDescriptionFixture"),
    },
    {
      name: "league-standings",
      lastRun: cronStatus?.leagueStandings?.lastRun || null,
      nextRun: cronStatus?.leagueStandings?.nextRun || null,
      status: cronStatus?.leagueStandings?.status || "idle",
      description: t("cronDescriptionStandings"),
    },
    {
      name: "promotion-relegation",
      lastRun: cronStatus?.promotionRelegation?.lastRun || null,
      nextRun: cronStatus?.promotionRelegation?.nextRun || null,
      status: cronStatus?.promotionRelegation?.status || "idle",
      description: t("cronDescriptionPromotion"),
    },
    {
      name: "round-transition",
      lastRun: cronStatus?.roundTransition?.lastRun || null,
      nextRun: cronStatus?.roundTransition?.nextRun || null,
      status: cronStatus?.roundTransition?.status || "idle",
      description: t("cronDescriptionRoundTransition"),
    },
  ];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("adminDashboardTitle")}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonGrid>
          <IonRow>
            <IonCol size="12">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>
                    <IonIcon icon={refreshOutline} className="ion-margin-end" />
                    {t("manualCronExecution")}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonText color="medium">
                    <p>
                      {t("manualCronDescription")}
                    </p>
                  </IonText>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>

          <IonRow>
            <IonCol size="12">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>{t("cronJobStatus")}</IonCardTitle>
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => queryClient.invalidateQueries("cronStatus")}
                  >
                    <IonIcon icon={refreshOutline} slot="start" />
                    {t("refresh")}
                  </IonButton>
                  <IonNote
                    style={{
                      fontSize: "12px",
                      marginTop: "8px",
                      display: "block",
                    }}
                  >
                    {t("autoRefreshEvery30")}
                  </IonNote>
                </IonCardHeader>
                <IonCardContent>
                  <IonList>
                    {cronJobs.map((job) => (
                      <IonItem key={job.name}>
                        <IonIcon
                          icon={getStatusIcon(job.status)}
                          color={getStatusColor(job.status)}
                          slot="start"
                        />
                        <IonLabel>
                          <h2>{job.description}</h2>
                          <p>
                            {t("lastRun")}: {job.lastRun || t("never")}
                            {job.nextRun && (
                              <>
                                <br />
                                {t("nextRun")}: {job.nextRun}
                              </>
                            )}
                          </p>
                        </IonLabel>
                        <IonBadge color={getStatusColor(job.status)} slot="end">
                          {job.status}
                        </IonBadge>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>

          <IonRow>
            <IonCol size="12">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>{t("manualTriggers")}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonGrid>
                    <IonRow>
                      <IonCol size="6">
                        <IonButton
                          expand="block"
                          color="primary"
                          onClick={() => handleExecuteJob("score-calculation")}
                          disabled={executeCronMutation.isLoading}
                        >
                          {executeCronMutation.isLoading &&
                          selectedJob === "score-calculation" ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <IonIcon icon={timeOutline} />
                          )}
                          {t("calculateScores")}
                        </IonButton>
                      </IonCol>
                      <IonCol size="6">
                        <IonButton
                          expand="block"
                          color="secondary"
                          onClick={() => handleExecuteJob("fixture-update")}
                          disabled={executeCronMutation.isLoading}
                        >
                          {executeCronMutation.isLoading &&
                          selectedJob === "fixture-update" ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <IonIcon icon={calendarOutline} />
                          )}
                          {t("updateFixtures")}
                        </IonButton>
                      </IonCol>
                    </IonRow>
                    <IonRow>
                      <IonCol size="6">
                        <IonButton
                          expand="block"
                          color="tertiary"
                          onClick={() => handleExecuteJob("league-standings")}
                          disabled={executeCronMutation.isLoading}
                        >
                          {executeCronMutation.isLoading &&
                          selectedJob === "league-standings" ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <IonIcon icon={refreshOutline} />
                          )}
                          {t("updateStandings")}
                        </IonButton>
                      </IonCol>
                      <IonCol size="6">
                        <IonButton
                          expand="block"
                          color="warning"
                          onClick={() =>
                            handleExecuteJob("promotion-relegation")
                          }
                          disabled={executeCronMutation.isLoading}
                        >
                          {executeCronMutation.isLoading &&
                          selectedJob === "promotion-relegation" ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <IonIcon icon={refreshOutline} />
                          )}
                          {t("processPromotionRelegation")}
                        </IonButton>
                      </IonCol>
                    </IonRow>
                    <IonRow>
                      <IonCol size="6">
                        <IonButton
                          expand="block"
                          color="success"
                          onClick={() => handleExecuteJob("round-transition")}
                          disabled={executeCronMutation.isLoading}
                        >
                          {executeCronMutation.isLoading &&
                          selectedJob === "round-transition" ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <IonIcon icon={refreshOutline} />
                          )}
                          {t("endRoundProcessPR")}
                        </IonButton>
                      </IonCol>
                      <IonCol size="6">
                        <IonButton
                          expand="block"
                          color="danger"
                          onClick={handleStopAll}
                          disabled={stopCronMutation.isLoading}
                        >
                          {stopCronMutation.isLoading ? (
                            <IonSpinner name="crescent" />
                          ) : (
                            <IonIcon icon={stopOutline} />
                          )}
                          {t("stopAllCronJobs")}
                        </IonButton>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>

          <IonRow>
            <IonCol size="12">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>{t("systemInformation")}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList>
                    <IonItem>
                      <IonLabel>
                        <h3>{t("environment")}</h3>
                        <p>{process.env.NODE_ENV || "development"}</p>
                      </IonLabel>
                    </IonItem>
                    <IonItem>
                      <IonLabel>
                        <h3>{t("lastStatusUpdate")}</h3>
                        <p>{new Date().toLocaleString()}</p>
                      </IonLabel>
                    </IonItem>
                    <IonItem>
                      <IonLabel>
                        <h3>{t("autoRefresh")}</h3>
                        <p>{t("every5Seconds")}</p>
                      </IonLabel>
                    </IonItem>
                  </IonList>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t("cronJobResult")}
          message={alertMessage}
          buttons={[t("okButton")]}
        />
      </IonContent>
    </IonPage>
  );
};

export default AdminDashboard;
